import { ChangeEvent, useEffect, useMemo, useState } from 'react';

import { OnboardInvitedMemberRequestDTO } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { GearbeltLoader } from '@gearbelt/ui/icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import clsx from 'clsx';
import { HTTPError } from 'ky';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import GearbeltLogo from '@/assets/gearbelt.svg';
import CheckIcon from '@/components/icons/check';
import CloseSmallIcon from '@/components/icons/close-small';
import { Form, FormFieldItem } from '@/components/ui/form';
import { PasswordInput } from '@/components/ui/password-input';
import { Toaster, useToast } from '@/components/ui/toaster';
import { ApiService } from '@/services/api-v2';
import { convertImageUrl } from '@/utils/image-url';

import { SignUpFooter } from '../signup';
import { IPasswordValidation, PasswordValidationsList } from '../signup/password-validations';
import { createCombinedPasswordValidationRegex, validatePassword } from '../signup/signup-form-utils';

const onboardFormSchema = z.object({
  password: z.string().regex(createCombinedPasswordValidationRegex()),
  confirmedPassword: z.string().min(1),
});

type OnboardFormData = z.infer<typeof onboardFormSchema>;

// Onboarding for an invitee whose email already resolves to an activated
// Sitemate identity (e.g. an AWA mechanic). The admin invite is the proof of
// email ownership, so there is no OTP — they only set a password.
export const OnboardPage = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [email, setEmail] = useState<string>('');
  const [workspaceInfo, setWorkspaceInfo] = useState<{ logoUrl: string | null; name: string }>({ logoUrl: '', name: '' });
  const [passwordValidation, setPasswordValidation] = useState<IPasswordValidation | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();

  const userIdSearchParam = useMemo(() => searchParams.get('userId'), [searchParams]);
  const codeSearchParam = useMemo(() => searchParams.get('code'), [searchParams]);
  const workspaceIdSearchParam = useMemo(() => searchParams.get('workspaceId'), [searchParams]);

  const form = useForm<OnboardFormData>({
    resolver: zodResolver(onboardFormSchema),
    defaultValues: { password: '', confirmedPassword: '' },
    mode: 'onChange',
  });
  const password = form.watch('password');
  const confirmedPassword = form.watch('confirmedPassword');

  useEffect(() => {
    loadDetails();
  }, []);

  async function loadDetails() {
    try {
      if (!userIdSearchParam || !codeSearchParam || !workspaceIdSearchParam) {
        throw new Error('This invitation link is invalid, please ask your admin for a new one');
      }
      const brandingResponse = await ApiService.GetWorkspaceBranding({ workspaceId: workspaceIdSearchParam });
      const { logoUrl, name } = await brandingResponse.json();
      setWorkspaceInfo({ logoUrl, name });

      const verifyResponse = await ApiService.VerifyActivationId({ activationId: codeSearchParam });
      const { email: verifiedEmail } = await verifyResponse.json();
      setEmail(verifiedEmail);
    } catch (error) {
      if (error instanceof HTTPError) {
        const parsedError = await error.response.json();
        toast.error(`An error occurred: ${parsedError.message}`);
      } else if (error instanceof Error) {
        toast.error(`An error occurred: ${error.message}`);
      } else {
        toast.error('An error occurred, please check your details and try again');
      }
    } finally {
      setLoading(false);
    }
  }

  const { mutateAsync: onboard, isPending } = useMutation({
    mutationFn: (values: OnboardInvitedMemberRequestDTO) => ApiService.OnboardInvitedMember(values),
    onSuccess: () => {
      toast.success('Your account is ready — please sign in.');
      navigate('/login');
    },
    onError: async (error: HTTPError) => {
      const message = await error.response
        .json<{ message: string }>()
        .then((json) => json.message)
        .catch(() => 'Please try again.');
      toast.error(`Onboarding error: ${message}`);
    },
  });

  function onPasswordChange(event: ChangeEvent<HTMLInputElement>): void {
    if (!event.target.value) {
      setPasswordValidation(null);
      return;
    }
    setPasswordValidation(validatePassword(event.target.value));
  }

  async function onSubmit() {
    if (!userIdSearchParam || !codeSearchParam || !workspaceIdSearchParam || !email) {
      toast.error('This invitation link is invalid. Please ask your admin for a new one.');
      return;
    }
    await onboard({
      userId: userIdSearchParam,
      activationId: codeSearchParam,
      email,
      password: form.getValues().password,
      workspaceId: workspaceIdSearchParam,
    });
  }

  const isSubmitDisabled = !password.length || !!form.formState.errors.password || password !== confirmedPassword || !email;

  return loading ? (
    <GearbeltLoader size={32} className="m-auto" />
  ) : (
    <>
      <div className="bg-grey-1 h-full w-full">
        <div className="z-10 m-auto mt-12 flex min-h-[277px] w-[400px] flex-col items-center gap-4 rounded bg-white px-8 py-6 shadow-lg">
          {workspaceInfo.logoUrl && <img src={convertImageUrl(workspaceInfo.logoUrl)} alt="workspace-logo" className="h-auto max-w-25 text-center" />}
          <header>
            <h2 className="pb-2 text-center text-xl font-medium">Set your Gearbelt password</h2>
            <p className="text-default-text text-center text-sm">
              You have been added to the
              <span className="px-0.5 font-medium">{workspaceInfo.name}</span>
              workspace. Set a password to finish setting up your account.
            </p>
          </header>

          <Form {...form}>
            <form className="space-between flex w-full flex-col gap-2">
              <FormFieldItem control={form.control} name="password" noErrorMessage={true}>
                {({ value, onChange }) => {
                  const { onChange: registerOnChange } = form.register('password');
                  return (
                    <PasswordInput
                      placeholder="Password"
                      value={value as string}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        onPasswordChange(e);
                        registerOnChange(e);
                        onChange(e.target.value);
                      }}
                    />
                  );
                }}
              </FormFieldItem>
              {passwordValidation && form.formState.errors.password && <PasswordValidationsList {...passwordValidation} />}
              <FormFieldItem control={form.control} name="confirmedPassword" noErrorMessage={true}>
                {({ value, onChange }) => {
                  const { onChange: registerOnChange } = form.register('confirmedPassword');
                  return (
                    <PasswordInput
                      placeholder="Confirm password"
                      value={value as string}
                      error={(!!value && password !== value) || !!form.formState.errors.confirmedPassword}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        registerOnChange(e);
                        onChange(e.target.value);
                      }}
                    />
                  );
                }}
              </FormFieldItem>
              {(form.formState.errors.confirmedPassword || (confirmedPassword && password !== confirmedPassword)) && (
                <div
                  className={clsx(
                    'flex items-center text-xs',
                    password.length === 0 || password !== confirmedPassword ? 'text-default-red' : 'text-default-green',
                  )}>
                  {password.length === 0 || password !== confirmedPassword ? <CloseSmallIcon /> : <CheckIcon />}
                  <span>Confirm password matches the password</span>
                </div>
              )}
              <Button size="lg" className="mt-3 w-full py-3 text-base" type="button" loading={isPending} onClick={onSubmit} disabled={isSubmitDisabled}>
                Set password
              </Button>
            </form>
          </Form>
        </div>

        <div className="flex flex-col items-center justify-center gap-1 pt-8">
          <img src={GearbeltLogo} alt="gearbelt-logo" className="h-8 w-auto" />
          <SignUpFooter className="text-sm" />
        </div>
      </div>
      <Toaster />
    </>
  );
};
